import { useState } from "react";
import type { ListRowContext } from "@/base/list/types";
import type { AuthedRequester } from "@/lib/brokerAuth";
import {
  archiveNotification,
  isUnread,
  markNotificationRead,
  noticeKind,
  notificationDestination,
  type InboxItem,
} from "@/lib/brokerNotifications";
import { KindIcon } from "@/components/KindIcon";
import { messageFor } from "@/lib/errors";

// The inbox list's row parts (BRK-S252): what kind of notice it is, what it
// says under its subject, and its Mark read and Archive controls. Their own
// file, so the list's definition file exports only the definition.

/** What kind of notice the row is: the mirror's label and icon, or a neutral
 *  "Update" for a type this build has not heard of, so a new PMS notice is
 *  never a blank row (BRK-S188). Shared by the inbox and the bell's panel. */
export function NoticeKindLabel({ item }: { item: InboxItem }) {
  const kind = noticeKind(item);
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-brk-dim">
      <KindIcon name={kind.icon} />
      <span>{kind.label}</span>
    </span>
  );
}

export function NoticeSubtitle({ item }: { item: InboxItem }) {
  const destination = notificationDestination(item);
  if (!item.body && !destination) return null;
  return (
    <>
      {item.body}
      {destination && (
        <span className="block font-medium text-brk-accent">
          {destination.label}
        </span>
      )}
    </>
  );
}

/** Mark read and Archive. Each names its notice, so a screen reader moving
 *  through the list hears which one it would act on. */
export function NoticeActions({
  item,
  request,
  list,
}: {
  item: InboxItem;
  request: AuthedRequester;
  list: ListRowContext;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function act(run: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await run();
      list.reload();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-3">
      {isUnread(item) && (
        <button
          type="button"
          disabled={busy}
          aria-label={`Mark “${item.subject}” read`}
          onClick={() => act(() => markNotificationRead(request, item.id))}
          className="text-xs font-medium text-brk-accent hover:underline disabled:opacity-50"
        >
          Mark read
        </button>
      )}
      <button
        type="button"
        disabled={busy}
        aria-label={`Archive “${item.subject}”`}
        onClick={() => act(() => archiveNotification(request, item.id))}
        className="text-xs font-medium text-brk-dim hover:underline disabled:opacity-50"
      >
        Archive
      </button>
      {error && (
        <span role="alert" className="text-xs text-brk-danger">
          {error}
        </span>
      )}
    </span>
  );
}
